import { ChangeDetectionStrategy, Component, Inject, output, PLATFORM_ID, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { IrisIconComponent, IconSizes } from '@sondermind/iris/icon';

import { SegmentPreviewService } from '../../services/segment-preview.service';
import { SegmentPreviewEvent, SegmentPreviewExportFile } from '../../models/segment-preview-event.model';
import { TimeAgoPipe } from '../../pipes/time-ago.pipe';

@Component({
  selector: 'segment-preview-tool',
  standalone: true,
  imports: [
    CommonModule,
    IrisIconComponent,
    TimeAgoPipe
  ],
  templateUrl: './segment-preview-tool.component.html',
  styleUrl: './segment-preview-tool.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentPreviewToolComponent {
  readonly iconSizes = IconSizes;
  readonly expandedEvents = signal(new Set<string>());
  readonly expanded = signal(false);

  readonly back = output<void>();

  constructor(
    public previewService: SegmentPreviewService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  onBack(): void {
    this.back.emit();
  }

  toggleExpanded(): void {
    this.expanded.update((v) => !v);
  }

  toggleEvent(eventId: string): void {
    this.expandedEvents.update((current) => {
      const next = new Set(current);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }

  onSearchInput(event: Event): void {
    const { value } = event.target as HTMLInputElement;
    this.previewService.searchQuery.set(value);
  }

  formatJson(event: SegmentPreviewEvent): string {
    return JSON.stringify(event.properties, null, 2);
  }

  exportEventsJson(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const events = this.previewService.events();
    if (events.length === 0) {
      return;
    }

    const payload: SegmentPreviewExportFile = {
      exportedAt: new Date().toISOString(),
      eventCount: events.length,
      events,
    };
    const json = JSON.stringify(payload, null, 2);
    const stamp = new Date().toISOString().replaceAll(/[:.]/g, '-');
    const filename = `segment-preview-export-${stamp}.json`;
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
